import React, { useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ItemHelper } from "../helpers/item-helper";
import getfile from "../../utils/getFile";
import { useLiffProfile } from "../../context/LiffProfileContext";
import { MaskInputHelper } from "../helpers/mask-input-helper";

const CouponItemDetail = ({ value = {}, userCoupon = false, children }) => {
  //  ต้องเรียก Hooks ทันที ไม่อยู่ใน if/return
  const { authToken, userId } = useLiffProfile();
  const { t, i18n } = useTranslation();
  const [image, setImage] = useState(null);
  const lang = i18n.language.toUpperCase();
  const seniorLayout = localStorage.getItem("seniorLayout") === "true";

  useEffect(() => {
    const loadImage = async () => {
      if (value.image || value.thumbnail) {
        try {
          const imgUrl = await getfile(
            value.image || value.thumbnail,
            authToken,
            userId
          );
          setImage(imgUrl);
        } catch (err) {
          console.warn("Image load error", err);
        }
      }
    };
    loadImage();
  }, [value.image, value.thumbnail]);

  const couponName = useMemo(
    () => ItemHelper.getName(value, lang),
    [value, lang]
  );
  const shortDescription = useMemo(
    () => ItemHelper.getShortDescription(value, lang),
    [value, lang]
  );
  const couponTag = useMemo(
    () => ItemHelper.getTag(value, lang),
    [value, lang]
  );
  const description = useMemo(
    () => ItemHelper.getDescription(value, lang),
    [value, lang]
  );
  const venue = useMemo(() => ItemHelper.getVenue(value, lang), [value, lang]);

  const periodDesc = useMemo(() => {
    const { ruleStartDate, ruleEndDate } = value;
    const locale = lang === "EN" ? "en-US" : "th-TH";
    const start = ruleStartDate
      ? new Intl.DateTimeFormat(locale, { dateStyle: "medium" }).format(
          new Date(ruleStartDate)
        )
      : null;
    const end = ruleEndDate
      ? new Intl.DateTimeFormat(locale, { dateStyle: "medium" }).format(
          new Date(ruleEndDate)
        )
      : null;
    if (start && end) return `${start} - ${end}`;
    if (start) return `${t("period_start")} ${start}`;
    if (end) return `${t("period_end")} ${end}`;
    return "";
  }, [value, lang, t]);

  //  แล้วค่อยเช็กเงื่อนไข
  if (!value || typeof value !== "object") return null;

  return (
    <div className="w-full flex flex-col gap-y-4">
      <div className="h-full flex flex-col bg-secondary-background-gray">
        <div className="w-full px-5 py-6 flex flex-col items-center gap-6">
          {image && (
            <div
              className="w-full h-auto rounded-2xl overflow-hidden bg-overlay-5"
            >
              <img
                className="object-contain w-full h-auto"
                src={image}
                alt="coupon"
                onError={() => setImage(null)}
              />
            </div>
          )}

          {children}

          <div className="w-full flex flex-col gap-y-2 text-left">
            <div
              className={`w-full font-semibold ${
                seniorLayout ? "text-2xl" : "text-xl"
              }`}
            >
              {couponName}
            </div>
            <div
              className={`w-full font-normal ${
                seniorLayout ? "text-xl" : "text-base"
              }`}
            >
              {!userCoupon && (
                <>
                  {t("special_privilege")}{" "}
                  <span className="text-primary-text">
                    {t("special_privilege_redeem", {
                      pointRedeem: MaskInputHelper.formatNumber(
                        value?.pointRedeem || 0
                      ),
                    })}
                  </span>
                </>
              )}
              <div dangerouslySetInnerHTML={{ __html: shortDescription }} />
            </div>
            <div className="w-full flex flex-col gap-y-2 border-t border-t-secondary-border mt-4 pt-4">
              {!userCoupon && (
                <>
                  <div
                    className={`font-semibold ${
                      seniorLayout ? "text-xl" : "text-base"
                    }`}
                  >
                    {t("coupon_redemption_period")}
                  </div>
                  <div
                    className={`font-normal ${
                      seniorLayout ? "text-xl" : "text-base"
                    }`}
                  >
                    {periodDesc}
                  </div>
                </>
              )}
              <div
                className={`w-full font-normal ${
                  seniorLayout ? "text-xl" : "text-base"
                }`}
                dangerouslySetInnerHTML={{ __html: couponTag }}
              />
            </div>
          </div>
        </div>

        <div className="w-full bg-overlay-5 flex flex-col gap-4 px-5 py-6">
          <div className="w-full text-left">
            <div
              className={`font-semibold ${
                seniorLayout ? "text-xl" : "text-base"
              }`}
            >
              {userCoupon ? t("terms_and_conditions") : t("redeem_conditions")}
            </div>
            <div
              className={`font-normal ${seniorLayout ? "text-lg" : "text-sm"}`}
              dangerouslySetInnerHTML={{ __html: description }}
            />
          </div>
          <div className="w-full text-left">
            <div
              className={`font-semibold ${
                seniorLayout ? "text-xl" : "text-base"
              }`}
            >
              {t("venue")}
            </div>
            <div
              className={`font-normal ${seniorLayout ? "text-lg" : "text-sm"}`}
            >
              {venue}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CouponItemDetail;
